import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import EditableText from "@/components/editor/EditableText";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_LINKS = [
  { href: "/story", label: "Origin" },
  { href: "/land", label: "Campsites" },
  { href: "/environment", label: "Tides" },
  { href: "/offgrid", label: "Off Grid" },
  { href: "/activities", label: "Activities" },
  { href: "/whales", label: "Whales" },
  { href: "/gallery", label: "Photography" },
  { href: "/videos", label: "Videos" },
  { href: "/faq", label: "FAQ" },
  { href: "/guestbook", label: "Guest Book" },
  { href: "/waitlist", label: "Follow the Build" },
];

export default function Footer() {
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleContact = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      setSubmitted(true);
    } catch (err) {
      console.error("Contact error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer data-testid="footer-section" className="bg-wc-forest text-wc-cream">
      {/* Contact form */}
      <div className="wc-section border-b border-wc-teal/20">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <EditableText contentKey="footer_heading" as="h2"
              className="font-display text-wc-cream text-3xl sm:text-4xl font-semibold mb-4"
              defaultValue="Get in touch." />
            <EditableText contentKey="footer_description" as="p"
              className="font-body text-wc-driftwood text-base leading-relaxed mb-6"
              defaultValue="Questions about Whale Cove? Want to talk through logistics for your group? Drop us a line. We're real people and we'll get back to you." />
            <div className="space-y-3">
              <a
                href="mailto:whalecoveoffgrid@gmail.com"
                className="font-body text-wc-teal text-sm flex items-center gap-2 hover:text-wc-brass transition-colors"
                data-testid="contact-email-link"
              >
                whalecoveoffgrid@gmail.com <ArrowRight className="w-3 h-3" />
              </a>
              <div className="flex items-center gap-4 mt-4">
                <a
                  href="#"
                  className="w-8 h-8 rounded-full bg-wc-teal/20 flex items-center justify-center hover:bg-wc-teal/40 transition-colors"
                  data-testid="instagram-social-link"
                  aria-label="Instagram"
                >
                  <svg className="w-4 h-4 text-wc-cream" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div>
            {submitted ? (
              <div className="py-8" data-testid="contact-success-message">
                <p className="font-display text-wc-cream text-xl font-semibold mb-2">Message sent.</p>
                <p className="font-body text-wc-driftwood text-sm">We'll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleContact} className="space-y-4" data-testid="contact-form">
                <Input
                  required
                  placeholder="Your name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="bg-wc-forest border-wc-teal/30 text-wc-cream placeholder:text-wc-driftwood/50 focus:ring-wc-teal"
                  data-testid="contact-name-input"
                />
                <Input
                  required
                  type="email"
                  placeholder="Email address"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="bg-wc-forest border-wc-teal/30 text-wc-cream placeholder:text-wc-driftwood/50 focus:ring-wc-teal"
                  data-testid="contact-email-input"
                />
                <textarea
                  required
                  placeholder="Your message"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  rows={4}
                  className="w-full rounded-md bg-wc-forest border border-wc-teal/30 text-wc-cream placeholder:text-wc-driftwood/50 px-3 py-2 font-body text-sm focus:outline-none focus:ring-2 focus:ring-wc-teal"
                  data-testid="contact-message-input"
                />
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-wc-forest text-wc-cream hover:bg-wc-teal font-body tracking-wide"
                  data-testid="contact-submit-button"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer bottom */}
      <div className="px-4 sm:px-8 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
            <div>
              <h3 className="font-display text-wc-cream text-lg font-semibold mb-2">Whale Cove Off Grid</h3>
              <p className="font-accent italic text-wc-driftwood/70 text-sm">Your private piece of the Bay of Fundy.</p>
              <p className="font-body text-wc-driftwood/50 text-xs mt-2">Digby Neck, Nova Scotia</p>
            </div>

            <div>
              <h4 className="font-body text-wc-driftwood text-xs uppercase tracking-widest mb-4">Navigate</h4>
              <div className="grid grid-cols-2 gap-y-2">
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    data-testid={`footer-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-body text-wc-driftwood/60 text-sm hover:text-wc-cream transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-body text-wc-driftwood text-xs uppercase tracking-widest mb-4">Two Sites</h4>
              <p className="font-body text-wc-driftwood/70 text-sm mb-1"><span className="text-wc-teal font-medium">Site One</span> — 3.5 acres · 175 ft · Opens 2027</p>
              <p className="font-body text-wc-driftwood/70 text-sm mb-1"><span className="text-wc-teal font-medium">Site Two</span> — 15 acres · 550 ft · Longer-term build</p>
              <a href="/waitlist" className="font-body text-wc-teal text-sm hover:text-wc-cream transition-colors inline-block mt-2">Follow the build &rarr;</a>
            </div>
          </div>

          {/* Google Maps — pinned to the actual Whale Cove coordinates */}
          <div className="mb-10 rounded-md overflow-hidden border border-wc-teal/20" data-testid="google-maps-embed">
            <iframe
              src="https://maps.google.com/maps?q=44.430722,-66.174944&hl=en&z=13&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="250"
              style={{ border: 0, filter: "grayscale(0.3) contrast(0.95)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Whale Cove Off Grid — 44°25'50.6&quot;N 66°10'29.8&quot;W, Digby Neck, Nova Scotia"
            />
          </div>

          <div className="border-t border-wc-teal/20 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="font-body text-wc-driftwood/40 text-xs">
              &copy; {new Date().getFullYear()} Whale Cove Off Grid. Digby Neck, Nova Scotia.
            </p>
            <p className="font-body text-wc-driftwood/30 text-xs">
              Site One opens 2027 · Site Two to follow
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
